import React from 'react';
import { Badge, Button, Col, Offcanvas, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatDate } from 'shared-pages/utils/helpers';
import { CollectionOrderStatusBadge } from '../collection-order-status-badge';
import {
  FaArrowUp,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaPhone,
  FaBox,
  FaTruck,
  FaClipboardCheck,
  FaFileAlt,
} from 'react-icons/fa';

interface QuickViewProps {
  show: boolean;
  onHide: () => void;
  selected: any;
}

export const QuickView: React.FC<QuickViewProps> = ({ show, onHide, selected }) => {
  const { t } = useTranslation();
  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      scroll
      backdrop={false}
      style={{ width: '420px' }}
    >
      <Offcanvas.Header
        closeButton
        closeVariant="white"
        style={{
          padding: '0 16px',
          borderBottom: '3px solid #85d700',
          background: 'linear-gradient(180deg, #495057 0%, #343a40 100%)',
          color: 'white',
          height: '42px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Offcanvas.Title style={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: '1' }}>
          {selected?.code ? (
            <>
              Αίτημα {selected.code}
              {selected?.isUrgent && (
                <Badge
                  bg="danger"
                  className="ms-2"
                  style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}
                >
                  <FaArrowUp /> Επείγον
                </Badge>
              )}
            </>
          ) : (
            'Συνοπτική Προβολή Αιτήματος'
          )}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body
        style={{
          padding: '0',
          background: '#f8f9fa',
        }}
      >
        {selected ? (
          <QuickViewContext selected={selected} />
        ) : (
          <div
            className="text-muted text-center"
            style={{
              padding: '40px 20px',
              fontSize: '0.95rem',
            }}
          >
            {t('Επιλέξτε μια γραμμή για να δείτε λεπτομέρειες.')}
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

const QuickViewContext: React.FC<any> = ({ selected }) => {
  const containers = selected.collectionOrdersContainers;
  const route =
    selected.routesLines && selected.routesLines[0] ? selected.routesLines[0].route : null;
  const collection = selected.collection;

  return (
    <div style={{ fontSize: '0.95rem' }}>
      {/* Order Information Section */}
      <Section title="Πληροφορίες Αιτήματος" icon={<FaFileAlt />}>
        <QuickViewContextLine label="Κωδικός Εντύπου" value={selected.code} icon={<FaFileAlt />} />

        <QuickViewContextLine
          label="Κατάσταση"
          value={
            <CollectionOrderStatusBadge
              id={selected?.statusId}
              value={selected?.statusId}
              name={selected?.collectionOrderState?.name}
            />
          }
        />

        <QuickViewContextLine
          label="Ημ/νία Αιτήματος"
          value={formatDate(selected.orderedDate)}
          icon={<FaCalendarAlt />}
        />
      </Section>

      {/* Collection Point Section */}
      <Section title="Σημείο Συλλογής" icon={<FaMapMarkerAlt />}>
        <QuickViewContextLine
          label="Σημείο"
          value={
            <div style={{ lineHeight: '1.6' }}>
              <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                {selected.collectionpoint.name}
              </div>
              <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                {selected.collectionpoint.addressLine1}
                <br />
                {selected.collectionpoint.addressArea}
                <br />
                {selected.collectionpoint.addressCity}
              </div>
            </div>
          }
          icon={<FaMapMarkerAlt />}
        />

        <QuickViewContextLine
          label="Επικοινωνία"
          value={
            <div style={{ lineHeight: '1.6' }}>
              {selected.contactPerson && (
                <div style={{ marginBottom: '4px' }}>
                  <strong>{selected.contactPerson}</strong>
                </div>
              )}
              {selected.collectionpoint.phone && (
                <div style={{ color: '#0066cc' }}>
                  <FaPhone size={12} /> {selected.collectionpoint.phone}
                </div>
              )}
              {selected.notes && (
                <div
                  style={{
                    color: '#6c757d',
                    fontSize: '0.85rem',
                    marginTop: '8px',
                    padding: '8px',
                    background: '#fff3cd',
                    borderRadius: '4px',
                    borderLeft: '3px solid #ffc107',
                  }}
                >
                  <strong>Σημειώσεις:</strong> {selected.notes}
                </div>
              )}
            </div>
          }
          icon={<FaPhone />}
        />
      </Section>

      {/* Containers Section */}
      <Section title="Συσκευασίες" icon={<FaBox />}>
        <QuickViewContextLine
          label="Κάδοι"
          value={
            containers && containers.length > 0 ? (
              <div>
                {containers.map((v, i) => {
                  if (v.containersQty)
                    return (
                      <Badge
                        key={v.id || i}
                        bg="info"
                        className="me-2 mb-1"
                        style={{
                          fontSize: '0.85rem',
                          fontWeight: 'normal',
                          padding: '6px 10px',
                        }}
                      >
                        {v.containersQty} × {v.containertype?.name}
                      </Badge>
                    );
                  return null;
                })}
              </div>
            ) : (
              <span style={{ color: '#6c757d' }}>Δεν υπάρχουν</span>
            )
          }
          icon={<FaBox />}
        />
      </Section>

      {/* Operations Section */}
      <Section title="Διαχείριση & Συλλογή" icon={<FaTruck />}>
        <QuickViewContextLine
          label="Συλλέκτης"
          value={selected.collector?.name || <span style={{ color: '#6c757d' }}>-</span>}
          icon={<FaTruck />}
        />

        {route && (
          <QuickViewContextLine
            label="Δρομολόγιο"
            value={
              <div>
                <Link
                  to={`route/${route.id}`}
                  style={{
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                >
                  {route.code}
                </Link>
                {route.routeDate && (
                  <div style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '2px' }}>
                    <FaCalendarAlt size={11} /> {formatDate(route.routeDate)}
                  </div>
                )}
              </div>
            }
          />
        )}

        {collection && (
          <QuickViewContextLine
            label="Συλλογή"
            value={
              <div>
                <Link
                  to={`/collection/${collection.id}`}
                  style={{
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                >
                  {collection.code}
                </Link>
                {collection.dateCollected && (
                  <div style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '2px' }}>
                    <FaCalendarAlt size={11} /> {formatDate(collection.dateCollected)}
                  </div>
                )}
              </div>
            }
            icon={<FaClipboardCheck />}
          />
        )}
      </Section>
    </div>
  );
};

const QuickViewContextLine: React.FC<any> = ({ label, value, icon }) => {
  return (
    <Row className="mb-3 align-items-start">
      <Col sm={12}>
        <div
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: '#6c757d',
            letterSpacing: '0.5px',
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {icon && <span style={{ opacity: 0.7 }}>{icon}</span>}
          {label}
        </div>
        <div
          style={{
            fontSize: '0.95rem',
            color: '#212529',
            paddingLeft: icon ? '22px' : '0',
          }}
        >
          {value}
        </div>
      </Col>
    </Row>
  );
};

// Section component for grouping related fields
const Section: React.FC<any> = ({ title, children, icon }) => {
  return (
    <div
      style={{
        background: 'white',
        padding: '16px',
        marginBottom: '12px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          fontSize: '0.9rem',
          fontWeight: 600,
          color: '#495057',
          marginBottom: '16px',
          paddingBottom: '8px',
          borderBottom: '2px solid #e9ecef',
          borderLeft: '3px solid #85d700',
          paddingLeft: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {icon && <span style={{ color: '#85d700' }}>{icon}</span>}
        {title}
      </div>
      {children}
    </div>
  );
};
